import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ScrollSwoosh.css';

const LEFT_X = 60;
const RIGHT_X = 440;
const SEGMENT_HEIGHT = 550; // px of vertical travel per left/right swing

const buildPath = (startY, endY) => {
  if (endY <= startY) return '';

  let d = `M ${LEFT_X} ${startY}`;
  let y = startY;
  let goingRight = true;

  while (y < endY) {
    const nextY = Math.min(y + SEGMENT_HEIGHT, endY);
    const midY = (y + nextY) / 2;
    const fromX = goingRight ? LEFT_X : RIGHT_X;
    const toX = goingRight ? RIGHT_X : LEFT_X;
    d += ` C ${fromX} ${midY}, ${toX} ${midY}, ${toX} ${nextY}`;
    y = nextY;
    goingRight = !goingRight;
  }

  return d;
};

const ScrollSwoosh = () => {
  const pathRef = useRef(null);
  const [docHeight, setDocHeight] = useState(0);
  const [startY, setStartY] = useState(0);

  const measure = useCallback(() => {
    const header = document.getElementById('about');
    const headerBottom = header
      ? header.getBoundingClientRect().bottom + window.scrollY
      : 0;
    setStartY(headerBottom);
    setDocHeight(document.documentElement.scrollHeight);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('load', measure);

    const resizeObserver = new ResizeObserver(() => measure());
    resizeObserver.observe(document.body);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('load', measure);
      resizeObserver.disconnect();
    };
  }, [measure]);

  useEffect(() => {
    if (!pathRef.current || docHeight === 0) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    const length = pathRef.current.getTotalLength();
    pathRef.current.style.strokeDasharray = `${length}`;

    if (prefersReducedMotion) {
      pathRef.current.style.strokeDashoffset = '0';
      return;
    }

    pathRef.current.style.strokeDashoffset = `${length}`;
    let ticking = false;

    const update = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0
        ? Math.min(Math.max(window.scrollY / scrollable, 0), 1)
        : 0;
      pathRef.current.style.strokeDashoffset = `${length * (1 - progress)}`;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [docHeight, startY]);

  if (docHeight === 0) return null;

  return (
    <div
      className="scroll-swoosh"
      style={{ height: docHeight }}
      aria-hidden="true"
    >
      <svg
        className="scroll-swoosh-svg"
        viewBox={`0 0 500 ${docHeight}`}
        preserveAspectRatio="none"
      >
        <path
          ref={pathRef}
          className="scroll-swoosh-path"
          d={buildPath(startY, docHeight)}
          fill="none"
        />
      </svg>
    </div>
  );
};

export default ScrollSwoosh;
